import { Routes } from '@angular/router';
import { ReloadComponent } from '@uigy/core/components/reload/reload.component';
import { UnauthorizedComponent } from '@uigy/core/components/unauthorized/unauthorized.component';
import { CanDeactivateGuard } from '@uigy/core/guards/can-deactivate.guard';
import { EnableGuard } from '@uigy/core/guards/enable.guard';
import { IemGuard } from '@uigy/core/guards/iem.guard';
import { IcpGuard } from '@uigy/core/guards/icp.guard';
import { LicensedGuard } from '@uigy/core/guards/licensed.guard';
import { MainLayoutComponent } from '@uigy/core/modules/layout/main-layout/main-layout.component';
import { Capability } from '@uigy/generated/graphql';
import { setCapabilitiesRouteData } from '@uigy/shared/capabilities/capabilities-route-data';
import { RoutesStatesNamesEnum } from './shared/enums/routesStatesNames.enum';
import { FullReportViewComponent } from '@uigy/modules/report/components/full-report-view/full-report-view.component';
import { DefaultPathService } from '@uigy/shared/services/default-path.service';
import { CapabilityGuardCanActivate, CapabilityGuardCanMatch } from '@uigy/core/guards/capability.guard';
import { OverviewPageComponent } from '@uigy/modules/overview/overiview-page/overview-page.component';
import { FindingsPageComponent } from '@uigy/modules/findings/components/findings-page/findings-page.component';
import { MostRecentEventsPageComponent } from '@uigy/modules/events/components/most-recent-events/most-recent-events-page.component';
import { IemPairedIcpsGuard } from '@uigy/core/guards/iem-paired-icps.guard';
import { DataSourcesComponent } from '@uigy/modules/settings/modules/data-sources/data-sources.component';

export const rootRoutes: Routes = [
  {
    path: RoutesStatesNamesEnum.report,
    component: FullReportViewComponent,
    loadChildren: () => import('./modules/report/report.module').then(m => m.ReportModule),
    canActivate: [IemPairedIcpsGuard],
  },
  {
    path: RoutesStatesNamesEnum.root,
    component: MainLayoutComponent,
    canActivate: [EnableGuard, LicensedGuard],
    children: [
      {
        path: RoutesStatesNamesEnum.reload,
        component: ReloadComponent,
      },
      {
        path: RoutesStatesNamesEnum.overview,
        canMatch: [EnableGuard, LicensedGuard],
        component: OverviewPageComponent,
      },
      {
        path: RoutesStatesNamesEnum.mostRecentEvents,
        canMatch: [EnableGuard, LicensedGuard, IemGuard],
        component: MostRecentEventsPageComponent,
      },
      {
        path: `${RoutesStatesNamesEnum.risks}/${RoutesStatesNamesEnum.compliance}`,
        canLoad: [EnableGuard, LicensedGuard],
        loadChildren: () => import('./modules/compliance/compliance.module').then(m => m.ComplianceModule),
        data: setCapabilitiesRouteData(Capability.ReadAssets),
      },
      {
        path: RoutesStatesNamesEnum.events,
        loadChildren: () => import('./modules/events/events.module').then(m => m.EventsModule),
        canMatch: [EnableGuard, LicensedGuard, CapabilityGuardCanMatch, IcpGuard],
        data: setCapabilitiesRouteData(Capability.ReadEvents),
      },
      {
        path: RoutesStatesNamesEnum.inventory,
        loadChildren: () => import('./modules/inventory/inventory.module').then(m => m.InventoryModule),
        canMatch: [EnableGuard, LicensedGuard, CapabilityGuardCanMatch, IcpGuard],
        data: setCapabilitiesRouteData(Capability.ReadAssets),
      },
      {
        path: `${RoutesStatesNamesEnum.settingsDataCollection}/${RoutesStatesNamesEnum.activeQueries}`,
        loadChildren: () => import('./modules/active-queries/active-queries.module').then(m => m.ActiveQueriesModule),
        canMatch: [EnableGuard, LicensedGuard, CapabilityGuardCanMatch, IcpGuard],
        data: setCapabilitiesRouteData(Capability.ReadActiveQueries),
      },
      {
        path: `${RoutesStatesNamesEnum.network}/${RoutesStatesNamesEnum.networkSummary}`,
        loadChildren: () => import('./modules/network-summary/network-summary.module').then(m => m.NetworkSummaryModule),
        canMatch: [EnableGuard, LicensedGuard, CapabilityGuardCanMatch, IcpGuard],
        data: setCapabilitiesRouteData(Capability.ReadNetwork),
      },
      {
        path: `${RoutesStatesNamesEnum.network}/${RoutesStatesNamesEnum.packetCaptures}`,
        loadChildren: () => import('./modules/packet-captures/packet-captures.module').then(m => m.PacketCapturesModule),
        canMatch: [EnableGuard, LicensedGuard, CapabilityGuardCanMatch, IcpGuard],
        data: setCapabilitiesRouteData(Capability.ReadNetwork),
      },
      {
        path: `${RoutesStatesNamesEnum.network}/${RoutesStatesNamesEnum.conversations}`,
        loadChildren: () => import('./modules/conversations/conversations.module').then(m => m.ConversationsModule),
        canMatch: [EnableGuard, LicensedGuard, CapabilityGuardCanMatch, IcpGuard],
        data: setCapabilitiesRouteData(Capability.ReadNetwork),
      },
      {
        path: `${RoutesStatesNamesEnum.settingsDataCollection}/${RoutesStatesNamesEnum.settingsDataSources}`,
        canMatch: [CapabilityGuardCanMatch],
        component: DataSourcesComponent,
        data: setCapabilitiesRouteData(Capability.ReadSensors),
      },
      {
        path: `${RoutesStatesNamesEnum.settingsDataCollection}/${RoutesStatesNamesEnum.policies}`,
        loadChildren: () => import('./modules/policies/policies.module').then(m => m.PoliciesModule),
        canMatch: [EnableGuard, LicensedGuard, CapabilityGuardCanMatch, IcpGuard],
        data: setCapabilitiesRouteData(Capability.ReadPolicies),
      },
      {
        path: RoutesStatesNamesEnum.networkMap,
        loadChildren: () => import('./modules/network-map/network-map.module').then(m => m.NetworkMapModule),
        canMatch: [EnableGuard, LicensedGuard, CapabilityGuardCanMatch, IcpGuard],
        data: setCapabilitiesRouteData(Capability.ReadNetwork),
      },
      {
        path: RoutesStatesNamesEnum.groups,
        loadChildren: () => import('./modules/groups/groups.module').then(m => m.GroupsModule),
        canMatch: [EnableGuard, LicensedGuard, CapabilityGuardCanMatch, IcpGuard],
        data: setCapabilitiesRouteData(Capability.ReadGroups),
      },
      {
        path: RoutesStatesNamesEnum.settings,
        loadChildren: () => import('./modules/settings/settings.module').then(m => m.SettingsModule),
        canMatch: [EnableGuard, LicensedGuard, CapabilityGuardCanMatch],
        data: setCapabilitiesRouteData(Capability.ReadAssets),
      },
      {
        path: RoutesStatesNamesEnum.singleAssetPage,
        loadChildren: () => import('./modules/assets/assets.module').then(m => m.AssetsModule),
        canMatch: [EnableGuard, LicensedGuard, CapabilityGuardCanMatch, IcpGuard],
        data: setCapabilitiesRouteData(Capability.ReadAssets),
      },
      {
        path: RoutesStatesNamesEnum.singleGroupPage,
        loadChildren: () => import('./modules/groups/groups.module').then(m => m.GroupsModule),
        canMatch: [EnableGuard, LicensedGuard, CapabilityGuardCanMatch, IcpGuard],
        data: setCapabilitiesRouteData(Capability.ReadGroups),
      },
      {
        path: `${RoutesStatesNamesEnum.risks}/${RoutesStatesNamesEnum.vulnerabilities}`,
        loadChildren: () => import('./modules/plugins/plugins.module').then(m => m.PluginsModule),
        canMatch: [EnableGuard, LicensedGuard, CapabilityGuardCanMatch, IcpGuard],
        data: setCapabilitiesRouteData(Capability.ReadVulnerabilities),
      },
      {
        path: `${RoutesStatesNamesEnum.risks}/${RoutesStatesNamesEnum.findings}`,
        // TODO: loadComponent: () => import('./modules/findings/findings-page/findings-page.component').then(m => m.FindingsPageComponent),
        canMatch: [EnableGuard, LicensedGuard, CapabilityGuardCanMatch, IcpGuard],
        component: FindingsPageComponent,
        data: setCapabilitiesRouteData(Capability.ReadVulnerabilities),
      },
      {
        path: RoutesStatesNamesEnum.icps,
        loadComponent: () => import('./modules/icps/components/icps/icps.component').then(m => m.IcpsComponent),
        canDeactivate: [CanDeactivateGuard],
        canActivate: [IemGuard, CapabilityGuardCanActivate],
        data: setCapabilitiesRouteData(Capability.ReadEmIcps),
      }
    ],
  },
  {
    path: RoutesStatesNamesEnum.unauthorized,
    component: UnauthorizedComponent,
  },
  {
    path: RoutesStatesNamesEnum.root,
    loadChildren: () => import('./modules/mini-layout/mini-layout.module').then(m => m.MiniLayoutModule),
  },
  {
    path: RoutesStatesNamesEnum.root,
    loadChildren: () => import('./modules/setup-wizard/setup-wizard.module').then(m => m.SetupWizardModule),
  },
  {
    path: '**',
    redirectTo: DefaultPathService.getStaticDefaultPath,
  },
];
